import { MD5 } from 'crypto-js';

/**
 * Gravatar URLを生成するユーティリティ関数
 * @param email メールアドレス
 * @param size アバターサイズ（デフォルト: 40）
 * @param defaultImage デフォルト画像の種類（デフォルト: 'identicon'）
 * @returns Gravatar URL
 */
export const getGravatarUrl = (
  email: string,
  size: number = 40,
  defaultImage: string = 'identicon'
): string => {
  if (!email) {
    return getDefaultAvatarUrl(size);
  }

  // メールアドレスを小文字に変換し、前後の空白を削除
  const normalizedEmail = email.toLowerCase().trim();

  // MD5ハッシュを生成
  const hash = MD5(normalizedEmail).toString();

  // Gravatar URLを構築
  const baseUrl = 'https://www.gravatar.com/avatar';
  const params = new URLSearchParams({
    s: size.toString(),
    d: defaultImage,
    r: 'pg', // 年齢制限のあるコンテンツを除外
  });

  return `${baseUrl}/${hash}?${params.toString()}`;
};

/**
 * デフォルトアバターURLを生成
 * @param size アバターサイズ
 * @returns デフォルトアバターURL
 */
export const getDefaultAvatarUrl = (size: number = 40): string => {
  // シンプルな幾何学模様のアバターを生成
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
  ];

  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  const backgroundColor = randomColor;
  const textColor = '#FFFFFF';

  // SVG形式のアバターを生成
  const svg = `
    <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="${backgroundColor}"/>
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${size * 0.4}" 
            fill="${textColor}" text-anchor="middle" dy="0.35em">?</text>
    </svg>
  `;

  // SVGをData URLに変換
  const encodedSvg = encodeURIComponent(svg);
  return `data:image/svg+xml,${encodedSvg}`;
};

/**
 * ユーザー名のイニシャルからアバターを生成
 * @param username ユーザー名
 * @param size アバターサイズ
 * @returns イニシャルアバターURL
 */
export const getInitialsAvatarUrl = (username: string, size: number = 40): string => {
  if (!username) {
    return getDefaultAvatarUrl(size);
  }

  // ユーザー名からイニシャルを取得（最大2文字）
  const initials = username
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');

  // 色を決定（ユーザー名のハッシュから）
  const colors = [
    '#FF6B6B',
    '#4ECDC4',
    '#45B7D1',
    '#96CEB4',
    '#FFEAA7',
    '#DDA0DD',
    '#98D8C8',
    '#F7DC6F',
    '#BB8FCE',
    '#85C1E9',
  ];

  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = username.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colorIndex = Math.abs(hash) % colors.length;
  const backgroundColor = colors[colorIndex];
  const textColor = '#FFFFFF';

  // SVG形式のアバターを生成
  const svg = `
    <svg width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="${backgroundColor}"/>
      <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="${size * 0.35}" 
            font-weight="bold" fill="${textColor}" text-anchor="middle" dy="0.35em">${initials}</text>
    </svg>
  `;

  // SVGをData URLに変換
  const encodedSvg = encodeURIComponent(svg);
  return `data:image/svg+xml,${encodedSvg}`;
};

/**
 * アバターURLを取得（優先順位: Gravatar > イニシャル > デフォルト）
 * @param email メールアドレス
 * @param username ユーザー名
 * @param size アバターサイズ
 * @returns アバターURL
 */
export const getAvatarUrl = (email?: string, username?: string, size: number = 40): string => {
  // メールアドレスがある場合はGravatarを優先
  if (email) {
    return getGravatarUrl(email, size);
  }

  // ユーザー名がある場合はイニシャルアバター
  if (username) {
    return getInitialsAvatarUrl(username, size);
  }

  // どちらもない場合はデフォルトアバター
  return getDefaultAvatarUrl(size);
};
